import { Component, OnDestroy, OnInit } from '@angular/core';
import { UntypedFormArray, UntypedFormBuilder, UntypedFormControl, UntypedFormGroup, Validators, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { UpdateFormValue, NgxsFormDirective } from '@ngxs/form-plugin';
import { Select, Store } from '@ngxs/store';
import { Observable, Subject, Subscription } from 'rxjs';
import { AcademicYearState } from '../academic-year/academic-year.state';
import { SaveService } from '../services/save.service';
import { RootState } from '../store/root.state';
import { IApplicationContent } from '../types';
import { Lead } from '../types/lead.type';
import { SafeValueValidator } from '../validators/safe-value.validator';
import { NumberLimitToDirective } from '../directives/number-limit-to.directive';
import { AddressLookaheadDirective } from '../directives/address-lookahead.directive';
import { PhoneDirective } from '../directives/phone.directive';
import { TrimValueDirective } from '../directives/trim-value.directive';
import { NgIf, NgFor, NgClass, AsyncPipe } from '@angular/common';

declare const NHAUtil: any;
declare const $: any;

@Component({
    selector: 'app-guardian',
    templateUrl: './guardian.component.html',
    styleUrls: ['./guardian.compoent.scss'],
    standalone: true,
    imports: [NgIf, FormsModule, NgxsFormDirective, ReactiveFormsModule, NgFor, TrimValueDirective, PhoneDirective, AddressLookaheadDirective, NumberLimitToDirective, NgClass, AsyncPipe]
})
export class GuardianComponent implements OnInit, OnDestroy {
	content$: Observable<IApplicationContent>;

	// Indexes used to help track guardians and households, for internal purpose only
	guardianIdx = 1;
	householdIdx = 1;
	guardianForm: UntypedFormGroup;
	academicYearApplying = 0;
	leadData: Lead = null;

	createLeadRequestQueue: Lead[] = [];
	createLeadRequestProcessing: Subject<void>;
	createLeadRequestProcessingSubscription: Subscription;

	schoolIsNY: boolean;

	constructor(
		private fb: UntypedFormBuilder,
		private saveService: SaveService,
		private store: Store
	) {
		this.content$ = this.store.select(RootState.content);
	}

	ngOnInit(): void {
		this.createForm();

		// autofill from CRM
		const params = new URLSearchParams(window.location?.search);
		this.addGuardian(params);

		this.store.select(AcademicYearState.selectedYear).subscribe((year) => {
			if (year) {
				this.academicYearApplying = year;
			}
		});

		this.schoolIsNY = this.store.selectSnapshot(RootState.content).stateCode === 'NY';

		// If a user uses form autofill we need to handle the lead create requests 1 at a time to allow proper deduping.
		// Feels like there should be a more optimal way to do this, good candidate for refactoring in the future.
		this.createLeadRequestProcessing = new Subject<void>();
		this.createLeadRequestProcessingSubscription = this.createLeadRequestProcessing.subscribe(() => {
			const lead = this.createLeadRequestQueue[0];

			this.saveService
				.createLead(lead)
				.toPromise()
				.then((returnedLead) => {
					this.leadData = returnedLead;
				})
				.finally(() => {
					this.createLeadRequestQueue.shift();

					if (this.createLeadRequestQueue.length > 0) {
						this.createLeadRequestProcessing.next();
					}
				});
		});
	}

	ngOnDestroy() {
		this.createLeadRequestProcessingSubscription.unsubscribe();
	}

	getControl(i: number, controlName: string): UntypedFormControl {
		return this.guardians.controls[i].get(controlName) as UntypedFormControl;
	}

	get guardians(): UntypedFormArray {
		return this.guardianForm.get('guardians') as UntypedFormArray;
	}

	createForm(): void {
		this.guardianForm = this.fb.group({
			guardians: this.fb.array([])
		});
	}

	addGuardian(params?: URLSearchParams): void {
		if (!params) {
			params = new URLSearchParams();
		}

		const newGuardian = this.fb.group(
			{
				internalGuardianId: this.guardianIdx++,
				firstName: [params.get('firstName') || '', [Validators.required, Validators.maxLength(100), SafeValueValidator]],
				lastName: [params.get('lastName') || '', [Validators.required, Validators.maxLength(100), SafeValueValidator]],
				email: [
					params.get('email') || '',
					[Validators.required, Validators.pattern('(^$|^[a-zA-Z0-9.!#$%&’*+/=?^_`{|}~-]+@[a-zA-Z0-9-]+(?:\\.[a-zA-Z0-9-]+)*$)'), Validators.maxLength(254)]
				],
				emailConfirm: [
					params.get('email') || '',
					[Validators.required, Validators.pattern('(^$|^[a-zA-Z0-9.!#$%&’*+/=?^_`{|}~-]+@[a-zA-Z0-9-]+(?:\\.[a-zA-Z0-9-]+)*$)'), Validators.maxLength(254)]
				],
				primaryPhone: [params.get('primaryPhone') || '', [Validators.required, Validators.pattern('\\(\\d{3}\\) \\d{3}-\\d{4}')]],
				secondaryPhone: [params.get('secondaryPhone') || '', Validators.pattern('(^$|\\(\\d{3}\\) \\d{3}-\\d{4})')],
				household: this.addHousehold(params),
				sameHousehold: [false, { updateOn: 'change' }],
				enrollmentPreferenceId: ''
			},
			{
				validators: (g: UntypedFormGroup) => {
					const email = g.get('email');
					const emailConfirm = g.get('emailConfirm');

					const emailValue: string = email?.value;
					const emailConfirmValue: string = emailConfirm?.value;

					if (emailConfirmValue === "") {
						return emailConfirm.setErrors({required: true})
					}

					if (emailValue.toLowerCase() !== emailConfirmValue.toLowerCase()) {
						
						return emailConfirm.setErrors({ mismatchEmail: true });
					} else {
						return emailConfirm.setErrors(null);
					}
				}
			}
		);
		this.guardians.push(newGuardian);

		setTimeout(() => {
			NHAUtil.configureTooltips();
		}, 700);
	}

	addHousehold(params: URLSearchParams): UntypedFormGroup {
		return this.fb.group({
			internalHouseholdId: this.householdIdx++,
			address: [params.get('address') || '', [Validators.required, Validators.maxLength(255), SafeValueValidator]],
			address2: [params.get('address2') || '', [Validators.maxLength(255), SafeValueValidator]],
			showAddress2: params.get('address2'),
			city: [params.get('city') || '', [Validators.required, Validators.maxLength(255), SafeValueValidator]],
			stateCode: [params.get('stateCode') || '', [Validators.required, SafeValueValidator]],
			zipCode: [params.get('zipCode') || null, [Validators.required, Validators.minLength(5), Validators.maxLength(5), SafeValueValidator]],
			lat: [],
			lng: [],
			isVerified: false
		});
	}

	getAddress(place: google.maps.places.PlaceResult, index: number) {
		let streetNumber = '',
			street = '',
			city = '',
			city2 = '',
			city3 = '',
			stateCode = '',
			zipCode = '',
			lat,
			lng;

		for (let i = 0; i < place.address_components.length; i++) {
			const addressType = place.address_components[i].types[0];
			switch (addressType) {
				case 'street_number':
					streetNumber = place.address_components[i].short_name;
					break;
				case 'route':
					street = place.address_components[i].short_name;
					break;
				case 'locality':
					city = place.address_components[i].long_name;
					break;
				case 'sublocality_level_1':
					city2 = place.address_components[i].long_name;
					break;
				case 'administrative_area_level_3':
					city3 = place.address_components[i].long_name;
					break;
				case 'administrative_area_level_1':
					stateCode = place.address_components[i].short_name;
					break;
				case 'postal_code':
					zipCode = place.address_components[i].short_name;
			}
		}
		const enteredAddress = this.guardians.controls[index].get('household.address').value;
		this.guardians.controls[index].get('household.address').setValue(streetNumber && street ? streetNumber + ' ' + street : enteredAddress);
		this.guardians.controls[index].get('household.city').setValue(city ? city : city2 ? city2 : city3 ? city3 : '');
		this.guardians.controls[index].get('household.stateCode').setValue(stateCode);
		this.guardians.controls[index].get('household.zipCode').setValue(zipCode);
		this.guardians.controls[index].get('household.lat').setValue(place.geometry.location.lat());
		this.guardians.controls[index].get('household.lng').setValue(place.geometry.location.lng());
		this.guardians.controls[index].get('household.address').updateValueAndValidity();
		this.guardians.controls[index].get('household.city').updateValueAndValidity();
		this.guardians.controls[index].get('household.stateCode').updateValueAndValidity();
		this.guardians.controls[index].get('household.zipCode').updateValueAndValidity();

		this.updateLeadData();
	}

	// Need to find first guardian in form and copy values to selected guardian
	sameHouseholdChange(i: number): void {
		const currentGuardian = this.guardians.controls[i] as UntypedFormGroup;

		if (currentGuardian.value.sameHousehold) {
			const firstGuardian = this.guardians.controls[0] as UntypedFormGroup;
			currentGuardian.controls.household.patchValue(firstGuardian.value.household);
		} else {
			currentGuardian.controls.household.reset();
			currentGuardian.controls.household.get('internalHouseholdId').setValue(this.householdIdx++);
		}
	}

	mapHouseholdsOnSave(): void {
		// Append address line 2 to address field for save
		this.guardians.controls.forEach((guardian, index) => {
			if (guardian.value.household.address2 !== '') {
				this.store.dispatch(
					new UpdateFormValue({
						value: { ...guardian.value.household, address: `${guardian.value.household.address} ${guardian.value.household.address2}` },
						path: `guardianState.guardianForm`,
						propertyPath: `guardians.${index}.household`
					})
				);
			}
		});

		if (this.guardians.controls.length > 1) {
			const firstGuardian = this.guardians.controls[0] as UntypedFormGroup;

			this.guardians.controls.forEach((guardian) => {
				if (guardian.value.internalGuardianId !== firstGuardian.value.internalGuardianId && guardian.value.sameHousehold) {
					(guardian as UntypedFormGroup).controls.household.patchValue(firstGuardian.value.household);
				}
			});
		}
	}

	removeGuardian(i: number): void {
		this.guardians.removeAt(i);
	}

	removeSecondaryAddress(i: number): void {
		const guardian = this.guardians.controls[i];

		guardian.get('household.showAddress2').setValue(false);
		guardian.get('household.address2').setValue('');
	}

	removeSecondaryPhone(i: number): void {
		const guardian = this.guardians.controls[i];

		// uses template variable to activate
		(guardian as any).secondaryPhone = '';
		guardian.get('secondaryPhone').setValue('');
	}

	updateLeadData(): void {
		const gData = this.guardians.controls[0].value;
		let sendUpdate = false;

		if ((this.leadData as any) !== 'Leads Disabled') {
			if (this.leadData) {
				if (this.leadData.academicYearId !== this.academicYearApplying) {
					this.leadData.academicYearId = this.academicYearApplying;
					sendUpdate = true;
				}
				if (this.leadData.guardian.firstName !== gData.firstName) {
					this.leadData.guardian.firstName = gData.firstName;
					sendUpdate = true;
				}
				if (this.leadData.guardian.lastName !== gData.lastName) {
					this.leadData.guardian.lastName = gData.lastName;
					sendUpdate = true;
				}
				if (this.leadData.guardian.email !== gData.email) {
					this.leadData.guardian.email = gData.email;
					sendUpdate = true;
				}
				if (this.leadData.guardian.primaryPhone !== gData.primaryPhone) {
					this.leadData.guardian.primaryPhone = this.parsePhone(gData.primaryPhone);
					sendUpdate = true;
				}
				if (this.leadData.guardian.household.address !== gData.household.address) {
					this.leadData.guardian.household.address = gData.household.address;
					sendUpdate = true;
				}
				if (this.leadData.guardian.household.city !== gData.household.city) {
					this.leadData.guardian.household.city = gData.household.city;
					sendUpdate = true;
				}
				if (this.leadData.guardian.household.stateCode !== gData.household.stateCode) {
					this.leadData.guardian.household.stateCode = gData.household.stateCode;
					sendUpdate = true;
				}
				if (this.leadData.guardian.household.zipCode !== gData.household.zipCode) {
					this.leadData.guardian.household.zipCode = gData.household.zipCode;
					sendUpdate = true;
				}
				if (sendUpdate) {
					this.saveService.updateLead(this.leadData).subscribe((lead) => {
						this.leadData = lead;
					});
				}
			} else if (gData.email && gData.firstName && gData.lastName) {
				// No lead data exists yet. Let's create a lead.
				const lead: Lead = new Lead();
				lead.academicYearId = this.academicYearApplying;
				lead.crmGuid = this.store.selectSnapshot(RootState.content).crmGuid;
				lead.guardian = gData;

				// If a create lead request is in flight, just queue up the next one to send later.
				if (this.createLeadRequestQueue.length === 0) {
					this.createLeadRequestQueue.push(lead);
					this.createLeadRequestProcessing.next();
				} else {
					this.createLeadRequestQueue.push(lead);
				}
			}
		}
		// Otherwise do nothing as Email is required for leads
	}

	parsePhone(data: string): string {
		return data.replace(/\D+/g, '');
	}
}
